import mapValues from "lodash/mapValues";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";

import {
  chatSettingsValueState,
  useChatData,
  useChatInteract,
} from "@chainlit/react-client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Translator } from "components/i18n";

import { chatSettingsOpenState } from "state/project";

import { FormInput, TFormInputValue } from "./FormInput";

export default function ChatSettingsModal() {
  const { chatSettingsValue, chatSettingsInputs, chatSettingsDefaultValue } =
    useChatData();

  const { updateChatSettings } = useChatInteract();
  const [chatSettingsOpen, setChatSettingsOpen] = useRecoilState(
    chatSettingsOpenState
  );

  const { handleSubmit, setValue, reset, watch } = useForm({
    defaultValues: chatSettingsValue,
  });
  const setChatSettingsValue = useSetRecoilState(chatSettingsValueState);
  const prevDialogOpenRef = useRef(chatSettingsOpen);
  const lastConfirmedModelRef = useRef<string | null>(null);
  const lastConfirmedPersonalityRef = useRef<string | null>(null);

  // Sync Recoil state with backend updates (e.g., when resuming threads)
  // Use lastConfirmedModelRef to prevent reverting to old cached values
  useEffect(() => {
    if (chatSettingsValue && !chatSettingsOpen) {
      const incomingModel = chatSettingsValue.model_selector;
      const incomingPersonality = chatSettingsValue.personality_selector;

      let correctedValue = { ...chatSettingsValue };
      let changed = false;

      // Only restore the last confirmed value when the backend value is empty.
      // If the backend provides a non-empty value, treat it as source of truth
      // and update the refs to avoid "stuck" selects.
      if (
        (!incomingModel || incomingModel === "") &&
        lastConfirmedModelRef.current
      ) {
        correctedValue.model_selector = lastConfirmedModelRef.current;
        changed = true;
      } else if (typeof incomingModel === "string" && incomingModel.trim()) {
        lastConfirmedModelRef.current = incomingModel;
      }

      if (
        (!incomingPersonality || incomingPersonality === "") &&
        lastConfirmedPersonalityRef.current
      ) {
        correctedValue.personality_selector = lastConfirmedPersonalityRef.current;
        changed = true;
      } else if (
        typeof incomingPersonality === "string" &&
        incomingPersonality.trim()
      ) {
        lastConfirmedPersonalityRef.current = incomingPersonality;
      }

      if (changed) {
        setChatSettingsValue(correctedValue);
      } else {
        setChatSettingsValue(chatSettingsValue);
      }
    }
  }, [chatSettingsValue, setChatSettingsValue, chatSettingsOpen]);

  // Reset form only when dialog transitions from closed to open
  useEffect(() => {
    if (chatSettingsValue && chatSettingsOpen && !prevDialogOpenRef.current) {
      // Dialog just opened, reset to show latest values (or last confirmed)
      const modelToUse =
        lastConfirmedModelRef.current || chatSettingsValue.model_selector;
      const personalityToUse =
        lastConfirmedPersonalityRef.current ||
        chatSettingsValue.personality_selector;

      const valueToReset = {
        ...chatSettingsValue,
        model_selector: modelToUse,
        personality_selector: personalityToUse,
      };
      reset(valueToReset);
    }
    prevDialogOpenRef.current = chatSettingsOpen;
  }, [chatSettingsOpen, chatSettingsValue, reset]);

  const handleClose = (open: boolean) => {
    if (!open) {
      reset(chatSettingsValue);
      setChatSettingsOpen(false);
    }
  };

  const handleConfirm = handleSubmit((data) => {
    const processedValues = mapValues(data, (x: TFormInputValue) =>
      x !== "" ? x : null
    );

    // Store the confirmed settings to prevent reverting to old cached values
    if (
      processedValues.model_selector &&
      typeof processedValues.model_selector === "string"
    ) {
      lastConfirmedModelRef.current = processedValues.model_selector;
    }

    if (
      processedValues.personality_selector &&
      typeof processedValues.personality_selector === "string"
    ) {
      lastConfirmedPersonalityRef.current = processedValues.personality_selector;
    }

    updateChatSettings(processedValues);
    setChatSettingsValue(processedValues);
    setChatSettingsOpen(false);
  });

  const handleReset = () => {
    reset(chatSettingsDefaultValue);
  };

  // Legacy setField compatibility layer
  const handleChange = () => { };

  const setFieldValue = (field: string, value: any) => {
    setValue(field, value);
  };

  const values = watch();

  return (
    <Dialog open={chatSettingsOpen} onOpenChange={handleClose}>
      <DialogContent
        id="chat-settings"
        className="min-w-[20vw] max-h-[85vh] flex flex-col gap-6 p-6"
      >
        <DialogHeader>
          <DialogTitle>
            <Translator path="chat.settings.title" />
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Translator path="chat.settings.customize" />
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col flex-grow overflow-y-auto gap-6 p-1">
          {chatSettingsInputs.map((input: any) => (
            <FormInput
              key={input.id}
              element={{
                ...input,
                value: values[input.id],
                onChange: handleChange,
                setField: setFieldValue,
              }}
            />
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleReset}>
            <Translator path="common.actions.reset" />
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" onClick={() => handleClose(false)}>
            <Translator path="common.actions.cancel" />
          </Button>
          <Button onClick={handleConfirm} id="confirm" autoFocus>
            <Translator path="common.actions.confirm" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
